import type { SearchOrderPayloadDto } from '@/types/request.dto';
import { formatTime } from '../format';
import type { HistoryQuery, HistoryRow, HistoryStatus } from '../history';
import type { PdsApiOrder } from './types';

export const STATUSES_BY_FILTER: Record<
  Exclude<HistoryStatus, 'all'>,
  string[]
> = {
  open: ['pending', 'in_progress', 'ready'],
  handed: ['served', 'completed', 'terminated'],
  cancelled: ['cancelled', 'failled', 'rejected'],
};

const PAYMENT_STATUS = {
  paid: 'paid',
  pending: 'payment_pending',
  refunded: 'refunded',
} as const;

const STATE_LABEL: Record<string, string> = {
  pending: 'Nouvelle',
  in_progress: 'En préparation',
  ready: 'Prête',
  served: 'Remise',
  completed: 'Remise',
  terminated: 'Remise',
  cancelled: 'Annulée',
  rejected: 'Refusée',
  failled: 'Échec',
};

const NEGATIVE_STATUSES = ['cancelled', 'rejected', 'failled'];

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date: Date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  );

const parseDay = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
};

const daysBack = (today: Date, days: number) => {
  const from = startOfDay(today);
  from.setDate(from.getDate() - days);
  return from;
};

// Jours du calendrier du poste, bornes incluses : « aujourd'hui » couvre jusqu'à 23:59:59.
export const periodRange = (
  query: Pick<HistoryQuery, 'period' | 'from' | 'to'>,
  now: number,
): { from?: Date; to?: Date } => {
  const today = new Date(now);
  switch (query.period) {
    case 'today':
      return { from: startOfDay(today), to: endOfDay(today) };
    case '7d':
      return { from: daysBack(today, 6), to: endOfDay(today) };
    case '30d':
      return { from: daysBack(today, 29), to: endOfDay(today) };
    case 'custom': {
      const first = parseDay(query.from);
      const second = parseDay(query.to);
      // Deux jours saisis à l'envers restent une période : le serveur refuserait une fin avant son début.
      const [from, to] =
        first && second && first > second ? [second, first] : [first, second];
      return {
        from: from ? startOfDay(from) : undefined,
        to: to ? endOfDay(to) : undefined,
      };
    }
    default:
      return {};
  }
};

// Les clés vides sont omises : une valeur `undefined` partirait dans l'URL sous la forme « undefined ».
export const buildSearchPayload = (
  query: HistoryQuery,
  storeId: string | undefined,
  now: number,
): SearchOrderPayloadDto => {
  const { from, to } = periodRange(query, now);
  const q = query.q.trim();

  return {
    pagination: true,
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    ...(storeId && { restaurantId: storeId }),
    ...(query.status !== 'all' && {
      status: STATUSES_BY_FILTER[query.status],
    }),
    ...(query.payment !== 'all' && {
      payed: [PAYMENT_STATUS[query.payment]],
    }),
    ...(query.channel !== 'all' && { channel: [query.channel] }),
    ...(q && { q }),
    ...(from && { fromDate: from.toISOString() }),
    ...(to && { toDate: to.toISOString() }),
    ...(query.minTotal !== null && { minTotal: query.minTotal }),
    ...(query.maxTotal !== null && { maxTotal: query.maxTotal }),
  };
};

const stateOf = (order: PdsApiOrder) => {
  const unpaid =
    order.status === 'pending' &&
    order.paymentStatus !== 'paid' &&
    order.paymentMethod !== 'cash';
  return unpaid
    ? 'En attente de paiement'
    : (STATE_LABEL[order.status] ?? order.status);
};

const sequenceOf = (orderNumber?: string | null) => {
  const match = /(\d+)$/.exec(orderNumber ?? '');
  return match ? Number(match[1]) : 0;
};

export const toHistoryRow = (order: PdsApiOrder): HistoryRow => {
  const itemCount = order.items
    ? order.items.reduce((sum, item) => sum + item.qty, 0)
    : null;
  const pickup = order.pickupTime ? Date.parse(order.pickupTime) : null;
  const reason = order.status === 'rejected' ? order.rejectReason : null;
  const when = order.orderDate ?? order.createdAt ?? order.pickupTime;

  return {
    id: order.id,
    number: sequenceOf(order.orderNumber),
    orderNumber: order.orderNumber || undefined,
    label: order.customerName || order.phone || 'Commande',
    day: when ? Date.parse(when) : Date.now(),
    state: stateOf(order),
    detail: [
      pickup !== null && `retrait ${formatTime(pickup)}`,
      itemCount !== null && `${itemCount} produit${itemCount > 1 ? 's' : ''}`,
      reason,
    ]
      .filter(Boolean)
      .join(' · '),
    amountCents: Math.round((order.totalPrice ?? 0) * 100),
    negative: NEGATIVE_STATUSES.includes(order.status),
    itemCount,
  };
};
